/**
 * UpdateFlowInit — on boot, offers an update (user confirms) when version or SW mismatch.
 */

import React from 'react';
import { useUpdateFlow } from '../contexts/UpdateFlowContext';
import { PWA_VERSION } from '../pwa-version';
import { applyServiceWorkerUpdate } from '../utils/apply-service-worker-update';
import { isLoginPathname } from '../utils/app-paths';

const INSTALLED_VERSION_KEY = 'pwa_installed_version';

export const UpdateFlowInit: React.FC = () => {
  const { phase, offerUpdate } = useUpdateFlow();

  React.useEffect(() => {
    if (phase === 'syncing') {
      return;
    }

    const maybeOffer = async (): Promise<void> => {
      const installedVersion = localStorage.getItem(INSTALLED_VERSION_KEY);

      if (!installedVersion) {
        localStorage.setItem(INSTALLED_VERSION_KEY, PWA_VERSION);
        return;
      }

      // Resolve SW state once, used in all branches below.
      let reg: ServiceWorkerRegistration | undefined;
      if ('serviceWorker' in navigator) {
        try {
          reg = await navigator.serviceWorker.getRegistration();
        } catch {
          // SW API unavailable; proceed without SW info
        }
      }

      // Sulla pagina di login (post-logout o primo accesso) non mostrare dialog.
      // Qualunque aggiornamento viene applicato silenziosamente: l'utente non sta
      // lavorando e un reload non causa perdita di dati.
      if (isLoginPathname(window.location.pathname)) {
        if (reg?.waiting) void applyServiceWorkerUpdate(reg);
        else if (installedVersion !== PWA_VERSION) localStorage.setItem(INSTALLED_VERSION_KEY, PWA_VERSION);
        return;
      }

      if (installedVersion !== PWA_VERSION) {
        if (reg?.waiting) {
          // New version + new SW waiting → show update dialog (normal flow).
          offerUpdate({
            reason: 'version',
            fromVersion: installedVersion,
            toVersion: PWA_VERSION,
          });
        } else {
          // New code is already running (SW already activated after previous update)
          // but the stamp was written by the old code before the reload.
          // Update the stamp silently — no dialog needed.
          localStorage.setItem(INSTALLED_VERSION_KEY, PWA_VERSION);
        }
        return;
      }

      // installedVersion === PWA_VERSION: apply any waiting SW silently, no dialog.
      if (reg?.waiting) void applyServiceWorkerUpdate(reg);
    };

    const timer = window.setTimeout(maybeOffer, 100);
    return () => clearTimeout(timer);
  }, [phase, offerUpdate]);

  return null;
};
